/**
 * Module dependencies.
 */

import { Title } from 'app/components/atoms/title/title';
import { useGetSaleDetail } from 'app/hooks/app-requests/sales/use-get-sale-detail';
import { useCrudRequest } from 'app/hooks/app-requests/crud/use-crud-request';
import { PosologyDuration, PosologyFrequency, PosologyQuantity } from 'app/types/posology';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Card, Tag, Button } from 'antd';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import { IdCopy } from 'app/components/atoms/id-copy/id-copy';
import {
  ShoppingOutlined,
  UserOutlined,
  ShopOutlined,
  MedicineBoxOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  CodeOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import ReactJson from 'react-json-view';

/**
 * Styled components.
 */

const Container = styled.div`
  padding: 20px;
  background: white;
`;

const HeaderSection = styled.div`
  margin-bottom: 24px;
`;

const SectionCard = styled(Card)`
  margin-bottom: 20px;

  .ant-card-head {
    background: #fafafa;
    padding: 12px 16px;
    min-height: auto;
  }

  .ant-card-body {
    padding: 16px;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr 200px 1fr;
  gap: 12px 24px;
  font-size: 14px;

  @media (max-width: 1200px) {
    grid-template-columns: 200px 1fr;
  }
`;

const Label = styled.div`
  font-weight: 600;
  color: #333;
`;

const Value = styled.div`
  color: #666;
`;

const MetaInfo = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
  font-size: 13px;
  color: #999;
`;

const ItemCard = styled(Card)`
  margin-bottom: 16px;
  border-left: 3px solid #1890ff;

  .ant-card-head {
    background: #f0f7ff;
    padding: 10px 16px;
    min-height: auto;
  }

  .ant-card-body {
    padding: 16px;
  }

  &.deleted {
    border-left-color: #ff4d4f;
    opacity: 0.7;

    .ant-card-head {
      background: #fff1f0;
    }
  }
`;

const ItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const ItemTitle = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: #333;
  flex: 1;
`;

const ItemBadges = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PosologySection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
`;

const PosologyCard = styled.div`
  background: #fafafa;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 12px;
  border-left: 3px solid #52c41a;
`;

const PosologyTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #333;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DosageCard = styled.div`
  background: white;
  padding: 12px;
  border-radius: 4px;
  margin-top: 12px;
  border: 1px solid #e8e8e8;
`;

const DosageTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
`;

/**
 * Get status color.
 */

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    completed: 'success',
    pending: 'warning',
    processing: 'processing',
    ready: 'default',
    canceled: 'error'
  };

  return colors[status] || 'default';
}

/**
 * Resolve duration.
 */

function resolveDuration(durationsList: PosologyDuration[] | undefined | null, code: string): string {
  return (durationsList ?? []).find(duration => duration.code === code)?.description.pt ?? 'N/A';
}

/**
 * Resolve quantity.
 */

function resolveQuantity(quantitiesList: PosologyQuantity[] | undefined | null, code: string): string {
  return (quantitiesList ?? []).find(quantity => quantity.code === code)?.description.pt ?? 'N/A';
}

/**
 * Resolve frequency.
 */

function resolveFrequency(frequenciesList: PosologyFrequency[] | undefined | null, code: string) {
  return (frequenciesList ?? []).find(frequency => frequency.code === code)?.name.pt ?? 'N/A';
}

/**
 * `SaleItemDetail` component.
 */

function SaleItemDetail({
  item,
  durationsList,
  frequenciesList,
  quantitiesList
}: {
  item: any;
  durationsList: PosologyDuration[] | undefined | null;
  frequenciesList: PosologyFrequency[] | undefined | null;
  quantitiesList: PosologyQuantity[] | undefined | null;
}): JSX.Element {
  const [showPosology, setShowPosology] = useState(false);

  return (
    <ItemCard className={item.deletedAt ? 'deleted' : ''} key={item.id} size={'small'}>
      <ItemHeader>
        <ItemTitle>
          <MedicineBoxOutlined style={{ marginRight: 8 }} />
          {item.productName || 'N/A'}
        </ItemTitle>
        <ItemBadges>{item.deletedAt && <Tag color={'error'}>{'DELETED'}</Tag>}</ItemBadges>
      </ItemHeader>

      <InfoGrid style={{ marginTop: 16 }}>
        <Label>{'Product Code'}</Label>
        <Value>{item.productCode || 'N/A'}</Value>

        <Label>{'External Item ID'}</Label>
        <Value>{item.externalItemId || 'N/A'}</Value>

        <Label>{'Quantity'}</Label>
        <Value>{item.quantity || 0}</Value>

        <Label>{'Status'}</Label>
        <Value>
          <Tag color={getStatusColor(item.status)}>{item.status?.toUpperCase()}</Tag>
        </Value>

        <Label>{'Price'}</Label>
        <Value>
          {'€'}
          {item.price || '0.00'}
        </Value>

        <Label>{'VAT Tax'}</Label>
        <Value>
          {item.vatTax}
          {'%'}
        </Value>

        <Label>{'Prescription Number'}</Label>
        <Value>{item.prescriptionNumber || 'N/A'}</Value>

        <Label>{'Renewal'}</Label>
        <Value>{item.renewal ? 'YES' : 'NO'}</Value>
      </InfoGrid>

      <MetaInfo>
        <span>
          {'Created: '}
          <DateTime date={item.createdAt} />
        </span>
        <span>
          {'Updated: '}
          <DateTime date={item.updatedAt} />
        </span>
      </MetaInfo>

      {item.posology && item.posology.length > 0 && (
        <PosologySection>
          <Button
            icon={<DownOutlined rotate={showPosology ? 180 : 0} />}
            onClick={() => setShowPosology(!showPosology)}
            size={'small'}
            type={'primary'}
          >
            {showPosology ? 'Hide Posology' : 'Show Posology'}
            {' ('}
            {item.posology.length}
            {')'}
          </Button>

          {showPosology && (
            <div style={{ marginTop: 16 }}>
              {item.posology.map((posology: any, posIndex: number) => (
                <PosologyCard key={posology.id}>
                  <PosologyTitle>
                    {'💊 Posology '}
                    {posIndex + 1}
                  </PosologyTitle>

                  <InfoGrid>
                    <Label>{'Description'}</Label>
                    <Value style={{ fontWeight: 500, color: '#333' }}>{posology.description || 'N/A'}</Value>

                    <Label>{'Instructions'}</Label>
                    <Value>{posology.instructions || 'N/A'}</Value>

                    <Label>{'Interval'}</Label>
                    <Value>{posology.interval || 'N/A'}</Value>

                    <Label>{'Rest'}</Label>
                    <Value>{posology.rest || 'N/A'}</Value>

                    <Label>{'Duration Value'}</Label>
                    <Value>{posology.durationValue || 'N/A'}</Value>

                    <Label>{'Duration Unit'}</Label>
                    <Value>{resolveDuration(durationsList, posology.durationUnit)}</Value>
                  </InfoGrid>

                  {posology.dosage && posology.dosage.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      {posology.dosage.map((dosage: any, dosageIndex: number) => (
                        <DosageCard key={dosage.id}>
                          <DosageTitle>
                            {'📋 Dosage '}
                            {dosageIndex + 1}
                          </DosageTitle>

                          <InfoGrid>
                            <Label>{'Quantity Value'}</Label>
                            <Value>{dosage.quantityValue || 'N/A'}</Value>

                            <Label>{'Quantity Unit'}</Label>
                            <Value>{resolveQuantity(quantitiesList, dosage.quantityUnit)}</Value>

                            <Label>{'Frequency Unit'}</Label>
                            <Value>{dosage.frequencyUnit || 'N/A'}</Value>

                            <Label>{'Frequency Value'}</Label>
                            <Value>{resolveFrequency(frequenciesList, dosage.frequencyValue)}</Value>

                            <Label>{'Created At'}</Label>
                            <Value>
                              <DateTime date={dosage.createdAt} />
                            </Value>

                            <Label>{'Updated At'}</Label>
                            <Value>
                              <DateTime date={dosage.updatedAt} />
                            </Value>
                          </InfoGrid>
                        </DosageCard>
                      ))}
                    </div>
                  )}
                </PosologyCard>
              ))}
            </div>
          )}
        </PosologySection>
      )}
    </ItemCard>
  );
}

/**
 * Export `SaleDetail` component.
 */

export function SaleDetail(): JSX.Element {
  const [translate] = useTranslation();
  const { userId, id } = useParams();
  const { data, isLoading } = useGetSaleDetail(id, userId);

  const { data: durationsList } = useCrudRequest<PosologyDuration[]>({
    endpoint: 'posologyEnumDuration',
    key: 'posologyEnumDuration',
    options: undefined
  });

  const { data: frequenciesList } = useCrudRequest<PosologyFrequency[]>({
    endpoint: 'posologyEnumFrequency',
    key: 'posologyEnumFrequency',
    options: undefined
  });

  const { data: quantitiesList } = useCrudRequest<PosologyQuantity[]>({
    endpoint: 'posologyEnumQuantity',
    key: 'posologyEnumQuantity',
    options: undefined
  });

  if (isLoading) {
    return <div style={{ padding: '24px' }}>{'Loading...'}</div>;
  }

  if (!data) {
    return <div style={{ padding: '24px' }}>{'Sale not found'}</div>;
  }

  return (
    <Container>
      <HeaderSection>
        <Title>{translate('users.sales.detail.title')}</Title>
      </HeaderSection>

      <SectionCard
        size={'small'}
        title={
          <span>
            <ShoppingOutlined style={{ marginRight: 8 }} />
            {'Sale Information'}
          </span>
        }
      >
        <InfoGrid>
          <Label>{'ID'}</Label>
          <Value>
            <IdCopy truncate={false}>{data.id}</IdCopy>
          </Value>

          <Label>{'Pharmacy'}</Label>
          <Value>{data.pharmacy?.name || 'N/A'}</Value>

          <Label>{'Provider'}</Label>
          <Value>{data.provider || 'N/A'}</Value>

          <Label>{'External User ID'}</Label>
          <Value>{data.externalUserId || 'N/A'}</Value>

          <Label>{'External Order ID'}</Label>
          <Value>{data.externalOrderNumber || 'N/A'}</Value>

          <Label>{'External Sale ID'}</Label>
          <Value>{data.externalSaleId || 'N/A'}</Value>

          <Label>{'VAT Number'}</Label>
          <Value>{data.vatNumber || 'N/A'}</Value>

          <Label>{'Status'}</Label>
          <Value>
            <Tag color={getStatusColor(data.status)}>{data.status?.toUpperCase()}</Tag>
          </Value>

          <Label>{'Is Annulled'}</Label>
          <Value>
            {data.isAnnulled ? (
              <Tag color={'error'} icon={<CloseCircleOutlined />}>
                {'YES'}
              </Tag>
            ) : (
              <Tag color={'success'} icon={<CheckCircleOutlined />}>
                {'NO'}
              </Tag>
            )}
          </Value>

          <Label>{'Items Count'}</Label>
          <Value>{data.lines?.length || 0}</Value>
        </InfoGrid>

        <MetaInfo>
          <span>
            {'Created: '}
            <DateTime date={data.createdAt} />
          </span>
          <span>
            {'Updated: '}
            <DateTime date={data.updatedAt} />
          </span>
        </MetaInfo>
      </SectionCard>

      <SectionCard
        size={'small'}
        title={
          <span>
            <UserOutlined style={{ marginRight: 8 }} />
            {'User Information'}
          </span>
        }
      >
        <InfoGrid>
          <Label>{'Name'}</Label>
          <Value>{data.user?.name || '-'}</Value>

          <Label>{'Email'}</Label>
          <Value>{data.user?.email || '-'}</Value>

          <Label>{'Phone Number'}</Label>
          <Value>{data.user?.phoneNumber || '-'}</Value>

          <Label>{'Birth Date'}</Label>
          <Value>{data.user?.birthDate ? <DateTime date={data.user.birthDate} /> : '-'}</Value>

          <Label>{'Gender'}</Label>
          <Value>{data.user?.gender?.toUpperCase() || '-'}</Value>

          <Label>{'Nationality'}</Label>
          <Value>{data.user?.nationality || '-'}</Value>
        </InfoGrid>
      </SectionCard>

      <SectionCard
        size={'small'}
        title={
          <span>
            <ShopOutlined style={{ marginRight: 8 }} />
            {'Pharmacy Information'}
          </span>
        }
      >
        <InfoGrid>
          <Label>{'Name'}</Label>
          <Value>{data.pharmacy?.name || '-'}</Value>

          <Label>{'Fiscal Name'}</Label>
          <Value>{data.pharmacy?.fiscalName || '-'}</Value>

          <Label>{'NIF'}</Label>
          <Value>{data.pharmacy?.nif || '-'}</Value>

          <Label>{'Address'}</Label>
          <Value>{data.pharmacy?.address || '-'}</Value>

          <Label>{'Postal Code'}</Label>
          <Value>{data.pharmacy?.postalCode || '-'}</Value>

          <Label>{'City'}</Label>
          <Value>{data.pharmacy?.city || '-'}</Value>

          <Label>{'Phone Number'}</Label>
          <Value>{data.pharmacy?.phoneNumber || '-'}</Value>

          <Label>{'Contact Name'}</Label>
          <Value>{data.pharmacy?.contactName || '-'}</Value>

          <Label>{'Contact Email'}</Label>
          <Value>{data.pharmacy?.contactEmail || '-'}</Value>
        </InfoGrid>
      </SectionCard>

      <div style={{ marginTop: 24 }}>
        {(data?.lines ?? []).map(item => (
          <SaleItemDetail
            durationsList={durationsList}
            frequenciesList={frequenciesList}
            item={item}
            key={item.id}
            quantitiesList={quantitiesList}
          />
        ))}
      </div>

      {data.metadata &&
        (() => {
          try {
            const parsedMetadata = JSON.parse(data.metadata);
            return (
              <SectionCard
                size={'small'}
                style={{ marginTop: 24 }}
                title={
                  <span>
                    <CodeOutlined style={{ marginRight: 8 }} />
                    {'Metadata'}
                  </span>
                }
              >
                <ReactJson
                  collapsed={1}
                  displayDataTypes={false}
                  displayObjectSize={true}
                  enableClipboard={true}
                  name={false}
                  src={parsedMetadata}
                  style={{ padding: '16px', fontSize: '13px' }}
                  theme={'rjv-default'}
                />
              </SectionCard>
            );
          } catch (error) {
            return (
              <SectionCard
                size={'small'}
                style={{ marginTop: 24 }}
                title={
                  <span>
                    <CodeOutlined style={{ marginRight: 8 }} />
                    {'Metadata'}
                  </span>
                }
              >
                <div style={{ padding: '16px', color: '#999', fontStyle: 'italic' }}>{'Invalid JSON format'}</div>
              </SectionCard>
            );
          }
        })()}
    </Container>
  );
}
